import { BookOpen, LayoutGrid, List, Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { KIND_LABEL, type AssetFacets, type FacetCounts } from '@/api/assets'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/components/ui/utils'
import { ASSET_KINDS, STORY_KIND, type LibrarySearch } from '@/routes/library'

/**
 * Filters for the library, driven by the **route search params** so every
 * view is a URL you can bookmark, paste into a chat and reopen.
 *
 * Facet counts come from the same `GET /api/assets` response as the items
 * (§5.1 `facets`), so a value that would return nothing is never offered.
 * Typing is debounced 250 ms before it reaches the URL — the address bar
 * should not flicker once per keystroke, and neither should the query.
 */

export const SEARCH_DEBOUNCE_MS = 250

export interface FilterBarProps {
  search: LibrarySearch
  facets: AssetFacets
  /** Partial patch merged into the route search; `undefined` clears a key. */
  onChange(patch: Partial<LibrarySearch>): void
  total?: number
  loading?: boolean
  className?: string
}

const STATUS_OPTIONS: { value: NonNullable<LibrarySearch['status']>; label: string }[] = [
  { value: 'ok', label: 'Working' },
  { value: 'broken', label: 'Broken' },
  { value: 'missing', label: 'Missing' },
  { value: 'all', label: 'Any file state' },
]

const TIER_OPTIONS: { value: NonNullable<LibrarySearch['tier']>; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'reviewed', label: 'Checked by a person' },
  { value: 'auto', label: 'Auto-published' },
  { value: 'none', label: 'Not published yet' },
]

const ALL = '__all__'

export function FilterBar({ search, facets, onChange, total, loading = false, className }: FilterBarProps) {
  const [q, setQ] = useState(search.q ?? '')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const committed = useRef(search.q ?? '')

  // Keep the box in step with the URL when navigation comes from elsewhere
  // (a back button, a command-palette jump) without fighting live typing.
  useEffect(() => {
    const next = search.q ?? ''
    if (next !== committed.current) {
      committed.current = next
      setQ(next)
    }
  }, [search.q])

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), [])

  const commit = (value: string) => {
    committed.current = value
    onChange({ q: value || undefined })
  }

  const onType = (value: string) => {
    setQ(value)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => commit(value), SEARCH_DEBOUNCE_MS)
  }

  const stories = search.kind === STORY_KIND
  // The Stories shelf is a place, not a filter: there only the search counts, and "Clear" keeps you on it.
  const activeCount = stories ? (search.q ? 1 : 0) : countActive(search)

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Row 1: kinds, the big affordance ----------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        <Chip selected={!search.kind} onClick={() => onChange({ kind: undefined })}>
          Everything
        </Chip>
        {ASSET_KINDS.map((kind) => (
          <Chip key={kind} selected={search.kind === kind} onClick={() => onChange({ kind })}>
            {KIND_LABEL[kind]}
          </Chip>
        ))}
        {/* Made stories: a shelf of pictures with their length, not assets (`StoryShelf`). The
            asset-only narrowing (category, world, file and published state) steps aside. */}
        <Chip
          selected={stories}
          icon={<BookOpen className="size-4" aria-hidden />}
          onClick={() => onChange({ kind: STORY_KIND, category: undefined, world: undefined, status: undefined, tier: undefined })}
        >
          Stories
        </Chip>

        <div className="ml-auto flex items-center gap-1 rounded-xl border border-line p-0.5" role="group" aria-label="View">
          <Button
            size="sm"
            variant={(search.view ?? 'grid') === 'grid' ? 'secondary' : 'ghost'}
            aria-pressed={(search.view ?? 'grid') === 'grid'}
            onClick={() => onChange({ view: 'grid' })}
            aria-label="Grid view"
          >
            <LayoutGrid aria-hidden />
          </Button>
          <Button
            size="sm"
            variant={search.view === 'list' ? 'secondary' : 'ghost'}
            aria-pressed={search.view === 'list'}
            onClick={() => onChange({ view: 'list' })}
            aria-label="List view"
          >
            <List aria-hidden />
          </Button>
        </div>
      </div>

      {/* Row 2: search + the narrowing selects ------------------------------ */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
          <Input
            type="search"
            value={q}
            onChange={(e) => onType(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (timer.current) clearTimeout(timer.current)
                commit(q)
              }
            }}
            placeholder={stories ? 'Search stories by title or idea' : 'Search by name, category or keyword'}
            aria-label={stories ? 'Search stories' : 'Search assets'}
            className="pl-9"
          />
        </div>

        {stories ? null : (
          <>
            <FacetSelect label="Category" value={search.category} counts={facets.category} onChange={(v) => onChange({ category: v })} />
            <FacetSelect label="World" value={search.world} counts={facets.world} onChange={(v) => onChange({ world: v })} />

            <Select value={search.status ?? ALL} onValueChange={(v) => onChange({ status: v === ALL ? undefined : (v as LibrarySearch['status']) })}>
              <SelectTrigger className="w-44" aria-label="File state">
                <SelectValue placeholder="Any file state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any file state</SelectItem>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                    {facets.status[o.value] != null ? ` (${facets.status[o.value]})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={search.tier ?? ALL} onValueChange={(v) => onChange({ tier: v === ALL ? undefined : (v as LibrarySearch['tier']) })}>
              <SelectTrigger className="w-52" aria-label="Published state">
                <SelectValue placeholder="Any published state" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any published state</SelectItem>
                {TIER_OPTIONS.filter((o) => o.value !== 'any').map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                    {facets.tier[o.value] != null ? ` (${facets.tier[o.value]})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}

        {activeCount > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setQ('')
              committed.current = ''
              onChange(stories ? { q: undefined } : { kind: undefined, category: undefined, world: undefined, status: undefined, tier: undefined, q: undefined })
            }}
          >
            <X aria-hidden />
            Clear {activeCount}
          </Button>
        ) : null}
      </div>

      <p className="text-xs text-ink-3" aria-live="polite">
        {loading ? 'Looking…' : total != null ? `${total.toLocaleString()} asset${total === 1 ? '' : 's'}` : ''}
      </p>
    </div>
  )
}

function FacetSelect({
  label,
  value,
  counts,
  onChange,
}: {
  label: string
  value: string | undefined
  counts: FacetCounts
  onChange(next: string | undefined): void
}) {
  const options = Object.entries(counts ?? {}).sort(([a], [b]) => a.localeCompare(b))
  // A facet with nothing in it is noise; a facet the URL already names stays
  // selectable even when this page's counts do not mention it.
  if (options.length === 0 && !value) return null
  const known = options.some(([k]) => k === value)
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
      <SelectTrigger className="w-44" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {label.toLowerCase()}</SelectItem>
        {!known && value ? <SelectItem value={value}>{value}</SelectItem> : null}
        {options.map(([key, n]) => (
          <SelectItem key={key} value={key}>
            {key} ({n})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** How many narrowing filters are on (the view mode and selection do not count). */
export function countActive(search: LibrarySearch): number {
  return [search.kind, search.category, search.world, search.status, search.tier, search.q].filter((v) => v !== undefined && v !== '').length
}
